import { useMaestroStore } from '../store/maestroStore';
import { useQobuzStore } from '../lib/useQobuz';
import { TransportCommand } from './TransportCommand';
import {
  isLocalLibraryTrack,
  isLocalTrackId,
  resolveLocalFileUrl,
  prefetchNextLocalTracks,
} from '../lib/playback-bridge';
import { isLimitedRenderer, getFormatIdForRenderer } from './types';
import { getSyncHandler } from '../handlers/ClientSyncHandler';
import { showImperativeToast } from '../providers/UIProvider';
import { routeStream, getMultizoneFormatId } from '../services/StreamRouter';
import { isIOSNativeApp } from '../utils/platform';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { useOperationProgressStore } from '../store/operationProgressStore';

/**
 * Pick a Qobuz-style format_id for a local library track. This mirrors the
 * convention used throughout the playback layer:
 *   7  → lossy (MP3/AAC/M4A)
 *   6  → 16-bit/44.1kHz lossless (CD quality)
 *   27 → hi-res lossless (>16/44)
 *
 * Multizone groups negotiate their own format via getMultizoneFormatId().
 * Limited renderers (no hi-res support) are pinned to 6 for lossless content.
 */
function pickFormatIdForLocalFile(
  track: any,
  renderer: any,
  isMultizone: boolean,
): number {
  const fmt = String(track?.format || '').toUpperCase();
  if (fmt === 'MP3' || fmt === 'AAC' || fmt === 'M4A') return 7;

  const rate = Number(track?.sample_rate || 0);
  const depth = Number(track?.bit_depth || 0);
  const isHires = (rate > 44100 && depth >= 24) || rate > 48000;

  if (isMultizone) {
    return isHires ? getMultizoneFormatId() : 6;
  }
  if (isLimitedRenderer(renderer)) return 6;
  return isHires ? 27 : 6;
}

/** Cooldown between _playTrack completions — blocks rapid duplicate dispatch (show-floor / quality-prefetch races). */
let _lastPlayTrackCompletedAt = 0;

/** Elapsed ms since `_playTrack` last finished (`Infinity` if never completed). Used by ghost playback watchdogs. */
export function msSinceLastPlayTrackCompleted(): number {
  return _lastPlayTrackCompletedAt === 0 ? Infinity : Date.now() - _lastPlayTrackCompletedAt;
}

// Test-playback mode: when active, the playing→stopped failure handler
// is suppressed so the Devices page can show its own format-specific UI.
let _testPlaybackMode = false;
export function setTestPlaybackMode(active: boolean) { _testPlaybackMode = active; }
export function isTestPlaybackMode(): boolean { return _testPlaybackMode; }

function _isRemoteRenderer(): boolean {
  const zone = useMaestroStore.getState().discovery?.activeZone;
  if (!zone || !zone.type) return false;
  const localTypes = ['browser', 'local', 'local-native'];
  const localIds = ['this_computer', 'local_computer', 'this_device'];
  return !localTypes.includes(zone.type) && !localIds.includes(zone.id);
}

function _isDsdFile(filePath: string | undefined | null): boolean {
  if (!filePath) return false;
  const ext = filePath.toLowerCase().split('.').pop() || '';
  return ext === 'dsf' || ext === 'dff' || ext === 'dsdiff';
}

class QueueControllerImpl {
  private _initialized = false;
  private _unsubscribe: (() => void) | null = null;
  private _suppressUntil = 0;

  private _lastPlayUriTime = 0;
  private _lastPlayUriFormatId = 0;
  private _failAdvancing = false;
  /** Prevents reentrant _advance when the store subscriber fires mid-await. */
  private _advancing = false;

  private _consecutiveFails = 0;
  private _consecutiveFailWindowStart = 0;

  private _consecutiveSkips = 0;

  /** Prevents overlapping _playTrack runs (watchdog + user) from racing two playUri calls to the renderer. */
  private _isPlayTrackInFlight = false;
  private _inFlightTrackId: string | null = null;

  /** Queue index of the track that was pre-buffered via SetNextAVTransportURI for gapless playback. */
  private _prebufferedForQueueIndex: number = -1;

  /** Set when playUri fails with "not found" — triggers auto-recovery when renderer returns */
  private _rendererLostAt = 0;
  private _rendererLostId: string | null = null;

  /** Suppress the playing→stopped advance for `ms` milliseconds (used during zone switches). */
  suppressAdvance(ms = 3000) {
    this._suppressUntil = Date.now() + ms;
    // Advance suppressed (zone switch cooldown)
  }

  init() {
    if (this._initialized) return;
    this._initialized = true;

    let previousState = useMaestroStore.getState().playbackState;
    let previousZoneCount = useMaestroStore.getState().discovery?.discoveredZones?.length || 0;

    this._unsubscribe = useMaestroStore.subscribe((state) => {
      const currentState = state.playbackState;

      if (currentState === 'playing' && previousState !== 'playing') {
        this._consecutiveFails = 0;
        this._rendererLostAt = 0;
        this._rendererLostId = null;
      }

      // Auto-recovery: when the renderer reappears after being lost, retry playback
      const zoneCount = state.discovery?.discoveredZones?.length || 0;
      if (this._rendererLostAt > 0 && this._rendererLostId && zoneCount > previousZoneCount) {
        const elapsed = Date.now() - this._rendererLostAt;
        if (elapsed < 120_000) { // within 2 minutes of loss
          const found = state.discovery?.discoveredZones?.some(
            (z: any) => z.id === this._rendererLostId
          );
          if (found && currentState === 'paused') {
            console.log('[QueueController] Renderer recovered — auto-resuming playback');
            this._rendererLostAt = 0;
            this._rendererLostId = null;
            this._playCurrentTrack();
          }
        } else {
          this._rendererLostAt = 0;
          this._rendererLostId = null;
        }
      }
      previousZoneCount = zoneCount;

      const wasActive = previousState === 'playing' || previousState === 'loading';
      if (wasActive && currentState === 'stopped') {
        const sincePlayUri = Date.now() - this._lastPlayUriTime;
        const tcRecentMs = 8000;
        const isTransportRecent = TransportCommand.isPlayUriRecent(tcRecentMs);

        console.log(`[QueueController] ${previousState}→stopped detected`,
          'sincePlayUri:', sincePlayUri, 'ms',
          '_lastPlayUriTime:', this._lastPlayUriTime,
          'TransportCommand.isPlayUriRecent:', isTransportRecent,
          'isRemote:', _isRemoteRenderer());

        if (Date.now() < this._suppressUntil) {
          console.log('[QueueController.subscribe] playing→stopped suppressed (zone switch)');
          previousState = currentState;
          return;
        }

        if (isIOSNativeApp() && document.visibilityState === 'hidden') {
          console.log('[QueueController.subscribe] playing→stopped suppressed — iOS app is backgrounded');
          previousState = currentState;
          return;
        }

        const isRapidFail = (sincePlayUri < 8000 && this._lastPlayUriTime > 0) || isTransportRecent;
        if (isRapidFail) {
          if (_testPlaybackMode) {
            console.log('[QueueController] playing→stopped during test playback — Devices page handles this');
            previousState = currentState;
            return;
          }
          this._handlePlayUriFailed();
          previousState = currentState;
          return;
        }

        if (_isRemoteRenderer()) {
          console.log('[QueueController.subscribe] playing→stopped on remote renderer — timer handles advance');
          previousState = currentState;
          return;
        }

        // Playback never started (resolve/playUri never completed) — not a natural track end.
        if (previousState === 'loading' && this._lastPlayUriTime === 0 && !isTransportRecent) {
          console.log('[QueueController] loading→stopped before playback started — ignoring end-of-track advance');
          previousState = currentState;
          return;
        }

        setTimeout(() => { this._advance('end'); }, 0);
      }
      previousState = currentState;
    });
  }

  destroy() {
    this._unsubscribe?.();
    this._initialized = false;
  }

  /** Replay the current queue track — used by auto-recovery after renderer loss */
  private _playCurrentTrack() {
    const store = useMaestroStore.getState();
    const idx = store.queueIndex;
    if (idx < 0 || idx >= store.queue.length) return;
    const item = store.queue[idx];
    if (!item) return;
    this._playTrack(item.track, idx);
  }

  private _handlePlayUriFailed() {
    if (this._failAdvancing) return;
    this._failAdvancing = true;

    const now = Date.now();
    if (now - this._consecutiveFailWindowStart > 30000) {
      this._consecutiveFails = 0;
      this._consecutiveFailWindowStart = now;
    }
    this._consecutiveFails++;

    const store = useMaestroStore.getState();
    const zoneName = store.discovery?.activeZone?.name || 'the renderer';
    const trackTitle = store.currentTrack?.title || 'This track';
    const isLocal = store.currentTrack?.source === 'local_library';
    const failReason = this._consecutiveFails >= 3
      ? 'consecutive_failures'
      : this._lastPlayUriFormatId === 6 && store.currentStreamFormatId === 27
        ? 'format_downgrade'
        : isLocal
          ? 'local_playback_error'
          : 'streaming_invalid_file';

    diagnosticLog(
      'error',
      'playback',
      `Track failed: ${trackTitle} — ${failReason}`,
      {
        trackId: store.currentTrack?.id,
        renderer: zoneName,
        sincePlayUri: now - this._lastPlayUriTime,
        reason: failReason,
      },
    );

    console.warn('[QueueController] playUri failed — stopped shortly after playUri.', {
      sincePlayUri: now - this._lastPlayUriTime,
      trackTitle,
      zoneName,
      formatId: this._lastPlayUriFormatId,
      consecutiveFails: this._consecutiveFails,
      isLocal,
    });

    if (this._consecutiveFails >= 3) {
      showImperativeToast(
        isLocal
          ? `Multiple tracks aren't playing. The file format may not be supported by "${zoneName}". Try playing on a different device.`
          : 'Multiple tracks on this album aren\'t playing. The streaming service is experiencing a temporary issue. Try a different album, or try again later.',
        10000,
      );
      store.setPlaybackState('paused');
      this._failAdvancing = false;
      return;
    }

    if (this._lastPlayUriFormatId === 6 && store.currentStreamFormatId === 27) {
      showImperativeToast('Playing at CD quality — hi-res temporarily unavailable', 10000);
    } else if (isLocal) {
      const fmt = String(store.currentTrack?.format || '').toUpperCase();
      const isDsd = fmt === 'DSF' || fmt === 'DFF';
      showImperativeToast(
        isDsd
          ? `Couldn't play "${trackTitle}" — DSD files require a compatible renderer. Try playing on a DSD-capable device.`
          : `Couldn't play "${trackTitle}" on "${zoneName}". The file may be in an unsupported format.`,
        10000,
      );
    } else {
      showImperativeToast(
        `Couldn't play "${trackTitle}" — the streaming service returned an invalid file. This is usually a temporary issue.`,
        10000,
      );
    }

    const currentQueueIndex = store.queueIndex;
    const queueLen = store.queue.length;
    console.log('[QueueController] Auto-advancing to next track. queueIndex:', currentQueueIndex, 'queueLength:', queueLen);

    setTimeout(async () => {
      try {
        await this._advance('failure');
        const newIndex = useMaestroStore.getState().queueIndex;
        console.log('[QueueController] Advance complete. New queueIndex:', newIndex, 'was:', currentQueueIndex);
      } catch (err) {
        console.error('[QueueController] Advance threw:', err);
      }
      this._failAdvancing = false;
    }, 500);
  }

  resetFailCounter() {
    this._consecutiveFails = 0;
  }

  private async _trySkipOnFailure(): Promise<boolean> {
    this._consecutiveSkips++;
    if (this._consecutiveSkips < 3) {
      const s = useMaestroStore.getState();
      const failedTrack = s.queue[s.queueIndex]?.track;
      const nextIdx = s.queueIndex + 1;
      if (nextIdx < s.queue.length) {
        console.warn('[QueueController] Skipping failed track, attempt', this._consecutiveSkips);

        const trackTitle = failedTrack?.title || 'Track';
        const isLocal = failedTrack?.source === 'local_library';
        if (isLocal) {
          showImperativeToast(
            `Skipped "${trackTitle}" — local file couldn't be played. The drive or network share may be disconnected.`,
            6000,
          );
        } else {
          showImperativeToast(
            `Skipped "${trackTitle}" — stream temporarily unavailable`,
            4000,
          );
        }

        s.setQueueIndex(nextIdx);
        const nextItem = s.queue[nextIdx];
        if (nextItem?.track) {
          this._isPlayTrackInFlight = false;
          this._inFlightTrackId = null;
          _lastPlayTrackCompletedAt = 0;
          await this._playTrack(nextItem.track);
          return true;
        }
      }
    }
    this._consecutiveSkips = 0;
    return false;
  }

  private async _advance(reason: 'end' | 'failure' | 'skip' = 'end') {
    if (this._advancing) {
      console.warn('[QueueController] _advance reentrant call suppressed', { reason });
      return;
    }
    this._advancing = true;
    try {
    const store = useMaestroStore.getState();
    if (store.queue.length === 0) {
      diagnosticLog('warn', 'playback', 'Queue state: 0 tracks remaining', { remaining: 0, totalLoaded: 0 });
      return;
    }

    const fromTrack = store.queue[store.queueIndex]?.track;
    const fromId = fromTrack?.id;
    const fromTitle = fromTrack?.title || '';

    const { queue, queueIndex, repeatMode, shuffled, shuffleOrder } = store;

    if (repeatMode === 'one') {
      const idx = shuffled && shuffleOrder.length > queueIndex ? shuffleOrder[queueIndex] : queueIndex;
      const item = queue[idx];
      if (item?.track) await this._playTrack(item.track);
      return;
    }

    const nextIndex = queueIndex + 1;

    if (nextIndex >= queue.length) {
      if (repeatMode === 'all') {
        store.setQueueIndex(0);
        const idx = shuffled && shuffleOrder.length > 0 ? shuffleOrder[0] : 0;
        const item = queue[idx];
        const toT = item?.track;
        diagnosticLog(
          'info',
          'playback',
          `Auto-advance from "${fromTitle}" to "${toT?.title || ''}"`,
          { fromTrackId: fromId, toTrackId: toT?.id, reason },
        );
        if (item?.track) await this._playTrack(item.track);
      } else {
        console.log('[QueueController] Playlist finished — stopping playback');
        diagnosticLog('warn', 'playback', 'Queue state: 0 tracks remaining', { remaining: 0, totalLoaded: queue.length });
        store.setPlaybackState('stopped');
      }
      return;
    }

    store.setQueueIndex(nextIndex);
    const idx = shuffled && shuffleOrder.length > nextIndex ? shuffleOrder[nextIndex] : nextIndex;
    const item = queue[idx];
    const toTrack = item?.track;
    diagnosticLog(
      'info',
      'playback',
      `Auto-advance from "${fromTitle}" to "${toTrack?.title || ''}"`,
      { fromTrackId: fromId, toTrackId: toTrack?.id, reason },
    );
    if (item?.track) await this._playTrack(item.track);
    } finally {
      this._advancing = false;
    }
  }

  private async _playTrack(track: any) {
    if (this._isPlayTrackInFlight) {
      console.warn('[QueueController] _playTrack in-flight guard hit — blocked duplicate', 'trackId:', track?.id);
      return;
    }
    const nowMs = Date.now();
    if (nowMs - _lastPlayTrackCompletedAt < 3000) {
      console.warn('[QC] _playTrack COOLDOWN — called within 3s of last completion, ignoring. track:', track?.title);
      return;
    }
    this._isPlayTrackInFlight = true;
    this._inFlightTrackId = track?.id != null ? String(track.id) : null;

    // ---------------------------------------------------------------
    // Defensive cross-surface teardown.
    //
    // Scenario: user is playing Ella Radio or Qobuz (either via the
    // browser's HTML5 audio element on "This Computer", or via a network
    // renderer) and then hits a library track. Without an explicit
    // teardown, whichever surface was playing before can keep running
    // alongside the new track — classic "two things playing at once".
    //
    // We aggressively pause/destroy every non-library surface before
    // dispatching the new track. Library tracks themselves are safe to
    // swap in-place via WebAudioRenderer (source swap) or SetAVTransportURI
    // (UPnP replace), so this only fires for foreign surfaces.
    // ---------------------------------------------------------------
    try {
      // Legacy radio engine teardown removed — Ella Radio uses the standard queue pipeline

      // 2) Flip the store's active source so PlaybackSnapshot readers
      //    (MiniPlayer, iOS Now Playing, queue sync) stop reading from
      //    radioStore and start reading from maestroStore.
      try {
        const s = useMaestroStore.getState();
        if (s.activeSource !== 'library') s.setActiveSource('library');
      } catch {}

      // 3) Pause the browser's local audio element. If we're about to
      //    route to a network renderer, this kills any Qobuz stream the
      //    browser was playing. If we're about to route locally,
      //    WebAudioRenderer will immediately set a new src and play.
      try {
        TransportCommand.pauseLocalAudioElement();
      } catch { /* best effort */ }
    } catch (e) {
      console.warn('[QC] cross-surface teardown failed (non-fatal):', e);
    }

    try {
      const store = useMaestroStore.getState();
      diagnosticLog(
        'info',
        'playback',
        `Track selected: ${track?.title || '?'} by ${track?.artist || '?'}`,
        {
          trackId: track?.id,
          source: track?.source,
          queuePosition: store.queueIndex,
          queueLength: store.queue.length,
        },
      );
      const mz = store.multizone;
      const isMultizone = mz.enabled && mz.groupedZones.length >= 2;
      let renderer = store.activeConnectorRenderer;

      // Clear stale renderer from BATON sync that isn't in locally discovered zones.
      // Without this, iOS (and any device receiving sync state from another device)
      // routes playUri to a renderer it can't reach, while also blocking local audio.
      if (renderer) {
        const zones = store.discovery?.discoveredZones || [];
        if (zones.length > 0 && !zones.some((z: any) => z.id === renderer!.id)) {
          console.log('[QC] Clearing stale renderer (not in local discoveredZones):', renderer!.id);
          store.setActiveConnectorRenderer(null);
          const az = store.discovery?.activeZone as any;
          if (az && az.type !== 'local' && az.type !== 'browser'
              && az.id !== 'this_computer' && az.id !== 'local_computer' && az.id !== 'this_device') {
            store.setActiveZone({ id: 'this_device', name: 'System Audio', type: 'local' } as any);
          }
          renderer = null;
        }
      }

      // Recover renderer from activeZone when activeConnectorRenderer is null.
      // The track source (local vs streaming) determines the audio URL, NOT the
      // renderer destination. A local file can play through any network renderer.
      if (!renderer && !isMultizone) {
        const az = store.discovery?.activeZone as any;
        if (az && _isRemoteRenderer()) {
          renderer = { id: az.id, name: az.name || az.id };
          store.setActiveConnectorRenderer(renderer);
          console.log('[QC] Recovered renderer from activeZone:', renderer.name);
        }
      }

      console.log('[QC] _playTrack:', track?.title, '→', renderer?.name || 'local');

      // If this track was pre-buffered via SetNextAVTransportURI, the renderer
      // already auto-transitioned to it (gapless). Just update the store — do NOT
      // send another SetAVTransportURI which would restart the track from 0:00.
      if (renderer && !isMultizone && this._prebufferedForQueueIndex === store.queueIndex) {
        console.log('[QC] Track was pre-buffered via NextURI — renderer auto-transitioned, skipping playUri');
        diagnosticLog(
          'info',
          'playback',
          `Track selected: ${track?.title || '?'} by ${track?.artist || '?'} (gapless)`,
          {
            trackId: track?.id,
            source: track?.source,
            queuePosition: store.queueIndex,
            queueLength: store.queue.length,
          },
        );
        this._prebufferedForQueueIndex = -1;
        store.setCurrentTrack(track);
        store.updateTime(0, track.duration || 0);
        store.setPlaybackState('playing');
        this._lastPlayUriTime = Date.now();
        this._consecutiveSkips = 0;
        queueMicrotask(() => this._prebufferNextTrack());
        return;
      }

      store.clearStreamCache();
      store.setCurrentTrack(track);
      store.updateTime(0, track.duration || 0);
      store.setPlaybackState('loading');

      {
        const totalLoaded = store.queue.length;
        const remaining = Math.max(0, totalLoaded - store.queueIndex - 1);
        if (remaining <= 2) {
          diagnosticLog('warn', 'playback', `Queue state: ${remaining} tracks remaining`, {
            remaining,
            totalLoaded,
          });
        }
      }

      // Detect local library tracks — never send local:track:* ids to Qobuz.
      const trackKey = String(track?.id || track?.trackId || '');
      const looksLocal = isLocalLibraryTrack(track) || trackKey.startsWith('local:track:');
      const hasFilePath = !!track?.file_path;
      const hasQobuzId = !!(track?.qobuzTrackId || track?.serviceTrackId);

      if (looksLocal || (hasFilePath && !hasQobuzId)) {
        if (looksLocal) {
          track = { ...track, source: 'local_library' };
        } else if (hasFilePath) {
          console.log('[QueueController] Local track detected by file_path fallback (source field missing/wrong):', track.source, '→ routing to local playback');
          track = { ...track, source: 'local_library' };
        }
        if (!track.file_path) {
          track = await this._hydrateLocalTrackFromLibrary(track);
        }
        if (!track?.file_path) {
          console.warn('[QueueController] Local track missing file_path:', trackKey || track?.title);
          showImperativeToast(
            `"${track?.title || 'This track'}" can't play — file path missing. Re-add it from your local library.`,
            8000,
          );
          store.setPlaybackState('paused');
          return;
        }
        await this._playLocalLibraryTrack(track, { isMultizone, renderer });
        this._consecutiveSkips = 0;
        return;
      }

      const qid = track.qobuzTrackId || track.serviceTrackId || track.id;
      if (isLocalTrackId(qid)) {
        console.warn('[QueueController] Refusing Qobuz resolution for local track id:', qid);
        store.setPlaybackState('paused');
        return;
      }
      if (!qid) {
        console.warn('[QueueController] No track ID for stream URL resolution');
        if (await this._trySkipOnFailure()) return;
        store.setPlaybackState('paused');
        return;
      }

      const formatId = isMultizone
        ? getMultizoneFormatId()
        : getFormatIdForRenderer(renderer);

      try {
        const data = await useQobuzStore.getState().getStreamUrl(String(qid), formatId);
        const url = data?.url || '';
        if (!url) {
          console.warn('[QueueController] No stream URL returned for:', qid);
          if (await this._trySkipOnFailure()) return;
          store.setPlaybackState('paused');
          return;
        }

        console.log('[Qobuz] stream URL fetched: trackId=' + qid + ' format=' + (data?.formatId || formatId));

        const resolvedFormatForLog = data?.formatId || formatId;
        diagnosticLog(
          'info',
          'playback',
          `Stream URL resolved: format ${resolvedFormatForLog}, ${url.length} chars`,
          {
            format: resolvedFormatForLog,
            urlLength: url.length,
            trackId: qid,
          },
        );

        const resolvedFormat = data?.formatId || formatId;
        const srcRate = data?.samplingRate || 0;
        const srcDepth = data?.bitDepth || 0;

        const decision = routeStream(url, srcRate, srcDepth, resolvedFormat);

        store.setCurrentStreamUrl(decision.effectiveUrl);
        store.setCurrentStreamFormatId(resolvedFormat);

        const prefetchAlternateFormatAfterPlay = () => {
          if (isMultizone) return;
          const altFormatId = resolvedFormat === 27 ? 6 : 27;
          const prefetchQid = String(qid);
          useQobuzStore.getState().getStreamUrl(prefetchQid, altFormatId).then(altData => {
            const s = useMaestroStore.getState();
            const curId = String(s.currentTrack?.qobuzTrackId || s.currentTrack?.serviceTrackId || s.currentTrack?.id || '');
            if (curId && curId !== prefetchQid) {
              console.warn('[QC] prefetch alternate skipped — current track changed', { prefetchQid, curId });
              return;
            }
            const altUrl = altData?.url || '';
            if (altUrl) {
              s.setAlternateStreamUrl(altUrl);
              s.setAlternateStreamFormatId(altData?.formatId || altFormatId);
            }
          }).catch(() => {});
        };

        // --- Multizone path: send one URL to all zones in parallel ---
        if (isMultizone) {
          diagnosticLog(
            'info',
            'transport',
            `playUri sent to multizone (${store.multizone.groupedZones.length} zones)`,
            {
              renderer: 'multizone',
              rendererType: 'multizone',
              trackId: track?.id,
              urlLength: (decision.effectiveUrl || '').length,
            },
          );
          await this._playTrackMultizone(decision.effectiveUrl, resolvedFormat, {
            ...track,
            formatId: resolvedFormat,
            format: data?.format,
            bitDepth: srcDepth,
            sampleRate: srcRate,
          });
          this._consecutiveSkips = 0;
          return;
        }

        // --- Single-zone path (unchanged) ---
        if (renderer) {
          this._lastPlayUriTime = Date.now();
          this._lastPlayUriFormatId = resolvedFormat;
          this._prebufferedForQueueIndex = -1;
          {
            const az = store.discovery?.activeZone as any;
            diagnosticLog(
              'info',
              'transport',
              `playUri sent to ${renderer.name || renderer.id}`,
              {
                renderer: renderer.name,
                rendererType: az?.transport_type || az?.type,
                trackId: track?.id,
                urlLength: (decision.effectiveUrl || '').length,
              },
            );
          }
          await TransportCommand.playUri(decision.effectiveUrl, renderer.id, resolvedFormat, {
            ...track,
            formatId: resolvedFormat,
            format: data?.format,
            bitDepth: decision.targetFormat.bitDepth || srcDepth,
            sampleRate: decision.targetFormat.sampleRate || srcRate,
            _streamRouterAction: decision.action,
            _streamRouterReason: decision.reason,
          });
          queueMicrotask(prefetchAlternateFormatAfterPlay);
          queueMicrotask(() => this._prebufferNextTrack());
        } else {
          diagnosticLog(
            'info',
            'transport',
            'playUri sent to local audio',
            {
              renderer: 'local',
              rendererType: 'browser',
              trackId: track?.id,
              urlLength: (decision.effectiveUrl || '').length,
            },
          );
          // Route through TransportCommand.playUri even without a network renderer
          // so _matchUsbDacSampleRate runs (exclusive mode + sample rate matching).
          // playUri's doPlay falls through to the local audio element when rendererId is empty.
          await TransportCommand.playUri(decision.effectiveUrl, '', resolvedFormat, {
            ...track,
            formatId: resolvedFormat,
            format: data?.format,
            bitDepth: decision.targetFormat.bitDepth || srcDepth,
            sampleRate: decision.targetFormat.sampleRate || srcRate,
          });
          queueMicrotask(prefetchAlternateFormatAfterPlay);
        }
        this._consecutiveSkips = 0;
      } catch (err: any) {
        const errMsg = String(err?.message || err || '');
        console.error('[QueueController] Stream URL resolution failed:', err);

        // "not found" means the renderer disappeared from discovery — skipping
        // to the next track won't help since the same renderer is unreachable.
        // Mark as lost so auto-recovery can resume when the renderer returns.
        const isRendererGone = errMsg.toLowerCase().includes('not found');
        if (isRendererGone) {
          const zone = store.discovery?.activeZone;
          this._rendererLostAt = Date.now();
          this._rendererLostId = zone?.id || null;
          console.warn('[QueueController] Renderer not found — pausing. Will auto-resume when renderer returns.');
          this._consecutiveSkips = 0;
          store.setPlaybackState('paused');
          return;
        }

        // "Command timeout" means the Connector's play_uri IPC didn't respond
        // in time — the renderer may still be processing the SOAP request.
        // Rapid-fire skipping floods the renderer with overlapping commands,
        // making things worse. Pause instead of cascading skips.
        const isTimeout = errMsg.toLowerCase().includes('command timeout') || errMsg.toLowerCase().includes('timed out');
        if (isTimeout) {
          console.warn('[QueueController] Command timeout — pausing instead of skip-cascade. Renderer may still be processing.');
          this._consecutiveSkips = 0;
          store.setPlaybackState('paused');
          return;
        }

        if (await this._trySkipOnFailure()) return;
        store.setPlaybackState('paused');
      }
    } finally {
      this._isPlayTrackInFlight = false;
      this._inFlightTrackId = null;
      _lastPlayTrackCompletedAt = Date.now();
    }
  }

  /**
   * Send a single stream URL to all multizone zones in parallel.
   * Uses Connector.multizonePlay (batch IPC) with fallback to
   * parallel individual playUri calls.
   */
  private async _playTrackMultizone(url: string, formatId: number, track: any) {
    const store = useMaestroStore.getState();
    const zones = store.multizone.groupedZones;

    this._lastPlayUriTime = Date.now();
    this._lastPlayUriFormatId = formatId;
    store.setPlaybackState('playing');

    try {
      const { isConnectorAvailable, multizonePlay, playUri: connectorPlayUri } = await import('../lib/connector');
      if (!isConnectorAvailable()) {
        console.warn('[QueueController] Multizone requires Connector');
        store.setPlaybackState('paused');
        return;
      }

      try {
        const results = await multizonePlay(
          zones.map(z => ({ renderer_id: z.zoneId, volume: z.actualVolume ?? null })),
          url,
          track?.title || '', track?.artist || '', track?.album || '',
          formatId,
          Math.round(track?.duration || 0),
          track?.artworkUrl || track?.artUrl,
        );
        const anyOk = results.some(r => r.success);
        if (anyOk) {
          store.setRendererHasStream(true);
          console.log('[QueueController] Multizone play results:', results);
        } else {
          console.error('[QueueController] All multizone zones failed:', results);
          store.setPlaybackState('paused');
        }
      } catch (batchErr) {
        console.warn('[QueueController] multizonePlay failed, falling back to parallel playUri:', batchErr);
        const results = await Promise.allSettled(
          zones.map(z =>
            connectorPlayUri(
              z.zoneId, url,
              track?.title || '', track?.artist || '', track?.album || '',
              formatId, Math.round(track?.duration || 0),
              track?.artworkUrl || track?.artUrl,
            )
          )
        );
        const anyOk = results.some(r => r.status === 'fulfilled');
        if (anyOk) store.setRendererHasStream(true);
        else store.setPlaybackState('paused');
      }
    } catch (err) {
      console.error('[QueueController] Multizone play failed:', err);
      store.setPlaybackState('paused');
    }
  }

  /**
   * Play a local library track, routing to the correct backend based on the
   * active zone:
   *   - Multizone group  → Connector.multizonePlay via _playTrackMultizone
   *   - Network renderer → TransportCommand.playUri (UPnP / iOS bridge)
   *   - Local / browser  → ClientTransport.playLocalStream (HTML5 audio)
   *
   * This mirrors the routing used for Qobuz tracks so local files are
   * first-class citizens of every playback path. Before this was added,
   * local files on a network DAC would silently fail because playLocalStream
   * bails out when a network zone is active.
   */
  private async _hydrateLocalTrackFromLibrary(track: any): Promise<any> {
    const trackKey = String(track?.id || track?.trackId || '');
    if (!trackKey.startsWith('local:')) return track;
    try {
      const { libraryService } = await import('../services/libraryService');
      const detail: any = await libraryService.getTrack(trackKey);
      const row = detail?.track || detail;
      const filePath = row?.file_path || row?.filePath;
      if (!filePath) return track;
      return {
        ...track,
        source: 'local_library',
        file_path: filePath,
        machine_id: row?.machine_id || track?.machine_id,
        format: row?.format || track?.format,
        bit_depth: row?.bit_depth ?? track?.bit_depth,
        sample_rate: row?.sample_rate ?? track?.sample_rate,
        duration: track?.duration || row?.duration || 0,
      };
    } catch (e) {
      console.warn('[QueueController] hydrate local track failed:', trackKey, e);
      return track;
    }
  }

  private async _playLocalLibraryTrack(
    track: any,
    ctx: { isMultizone: boolean; renderer: any },
    retried = false,
  ): Promise<void> {
    const store = useMaestroStore.getState();
    const { isMultizone } = ctx;
    // Renderer recovery: ctx.renderer may be null if the stale-renderer cleanup
    // in _playTrack cleared it. Re-derive from activeZone so local files play
    // through the user's selected network renderer, not the USB DAC/local path.
    let renderer = ctx.renderer;
    if (!renderer && !isMultizone && _isRemoteRenderer()) {
      const az = store.discovery?.activeZone as any;
      if (az) {
        renderer = { id: az.id, name: az.name || az.id };
        store.setActiveConnectorRenderer(renderer);
        console.log('[QC] _playLocalLibraryTrack: recovered renderer from activeZone:', renderer.name);
      }
    }

    if (!isMultizone && !renderer && _isDsdFile(track.file_path)) {
      console.warn('[QueueController] DSD playback blocked on System Audio', {
        file_path: track.file_path,
        renderer_id: store.discovery?.activeZone?.id,
      });
      store.setPlaybackState('paused');
      showImperativeToast(
        'DSD files require a network renderer with DSD support. System Audio (this computer) cannot decode DSD.',
        6000,
      );
      return;
    }

    // Desktop + network renderer: use play_local_file IPC directly.
    // Rust handles proxy URL construction with the renderer's actual IP,
    // then sends SOAP SetAVTransportURI + Play in one call.
    // This avoids the register_file_url path which doesn't know the
    // renderer IP and produces an unreachable 127.0.0.1 URL.
    const { isConnectorAvailable, playLocalFile } = await import('../lib/connector');
    const { isHomeLibraryHost } = await import('../services/localLibraryIpcGate');
    const isDesktopNetworkRenderer = !isMultizone
      && isConnectorAvailable()
      && isHomeLibraryHost()
      && !!renderer?.id
      && _isRemoteRenderer();

    if (isDesktopNetworkRenderer) {
      console.log('[QC] local track → play_local_file IPC (desktop+network renderer)', {
        renderer: renderer.name || renderer.id,
        file: String(track.file_path || '').split('/').pop(),
      });

      try {
        const proxyUrl = await playLocalFile(
          track.file_path,
          renderer.id,
          track.title || '',
          track.artist || '',
          track.album || '',
          Math.round(track.duration || 0),
          track.embedded_cover_hash || track.folder_cover_hash || null,
          track.sample_rate ?? null,
          track.bit_depth ?? null,
          track.channels ?? null,
          track.file_size ?? null,
        );

        const s = useMaestroStore.getState();
        if (s.currentTrack?.id !== track.id && s.currentTrack?.file_path !== track.file_path) {
          console.log('[QC] local track changed during play_local_file — aborting');
          return;
        }

        s.setCurrentStreamUrl(proxyUrl);
        s.setCurrentStreamFormatId(pickFormatIdForLocalFile(track, renderer, false));
        s.setRendererHasStream(true);
        s.setPlaybackState('buffering');
        const opId = useOperationProgressStore.getState().beginOperation('Buffering audio...', {
          progress: 0.15,
        });
        window.setTimeout(() => {
          const now = useMaestroStore.getState();
          const stillCurrent =
            now.currentTrack?.id === track.id || now.currentTrack?.file_path === track.file_path;
          if (stillCurrent && now.playbackState === 'buffering') {
            now.setPlaybackState('playing');
          }
          useOperationProgressStore.getState().endOperation(opId);
        }, 1500);
        this._lastPlayUriTime = Date.now();
        this._lastPlayUriFormatId = pickFormatIdForLocalFile(track, renderer, false);
        this._consecutiveSkips = 0;

        const { getClientTransport } = await import('../handlers/ClientTransportHandler');
        const ws = getClientTransport().getWebSocket();
        queueMicrotask(() => prefetchNextLocalTracks(ws, track.machine_id));
        return;
      } catch (err: any) {
        console.error('[QC] play_local_file IPC failed:', err?.message || err);

        if (!retried && track.file_path) {
          console.log('[QC] retrying local track via play_local_file');
          return this._playLocalLibraryTrack(track, ctx, true);
        }

        const errMsg = String(err?.message || err || '');
        const isNotFound = errMsg.toLowerCase().includes('not found') || errMsg.toLowerCase().includes('no such file');
        const trackTitle = track.title || 'This track';
        if (isNotFound) {
          showImperativeToast(
            `"${trackTitle}" — file not found. The drive or network share may be disconnected.`,
            8000,
          );
        } else {
          showImperativeToast(
            `Couldn't play "${trackTitle}" — ${errMsg.slice(0, 120)}`,
            8000,
          );
        }

        const s2 = useMaestroStore.getState();
        if (s2.currentTrack?.id === track.id || s2.currentTrack?.file_path === track.file_path) {
          s2.setPlaybackState('paused');
        }
        return;
      }
    }

    // Fallback: System Audio, iOS/BATON, multizone, or no renderer —
    // use the URL-first resolveLocalFileUrl path.
    const { getClientTransport } = await import('../handlers/ClientTransportHandler');
    const ct = getClientTransport();
    const ws = ct.getWebSocket();

    let url: string;
    try {
      url = await resolveLocalFileUrl(track.file_path, ws, track.machine_id);
    } catch (err: any) {
      console.error('[QueueController] local file resolve failed:', err?.message || err);
      const errMsg = String(err?.message || err || '');
      const trackTitle = track.title || 'This track';
      showImperativeToast(
        `"${trackTitle}" — can't access file. The drive or network share may be disconnected.`,
        8000,
      );
      const s = useMaestroStore.getState();
      if (s.currentTrack?.id === track.id || s.currentTrack?.file_path === track.file_path) {
        s.setPlaybackState('paused');
      }
      return;
    }

    // If the user switched tracks while we were resolving, abandon this dispatch.
    const s = useMaestroStore.getState();
    const stillCurrent =
      s.currentTrack?.id === track.id || s.currentTrack?.file_path === track.file_path;
    if (!stillCurrent) {
      console.log('[QueueController] local track changed during URL resolve — aborting');
      return;
    }

    const formatId = pickFormatIdForLocalFile(track, renderer, isMultizone);
    const trackForTransport = {
      ...track,
      streamUrl: url,
      formatId,
      format: track.format,
      bitDepth: track.bit_depth,
      sampleRate: track.sample_rate,
      artUrl: track.artUrl || track.artworkUrl || track.cover_art_url,
    };

    store.setCurrentStreamUrl(url);
    store.setCurrentStreamFormatId(formatId);

    try {
      if (isMultizone) {
        await this._playTrackMultizone(url, formatId, trackForTransport);
      } else if (!renderer?.id || !_isRemoteRenderer()) {
        // System Audio / browser — HTML5 audio via WebAudioRenderer (required for
        // remote tunnel URLs; playUri + Tauri exclusive path cannot stream trycloudflare).
        this._lastPlayUriTime = Date.now();
        this._lastPlayUriFormatId = formatId;
        console.log('[QC] local track → playLocalStream', {
          zone: store.discovery?.activeZone?.name || 'System Audio',
          file: String(track.file_path || '').split('/').pop(),
        });
        const ct = getClientTransport();
        await ct.playLocalStream({ ...trackForTransport, streamUrl: url });
      } else {
        this._lastPlayUriTime = Date.now();
        this._lastPlayUriFormatId = formatId;
        console.log('[QC] local track → playUri', {
          renderer: renderer?.name || '(network)',
          formatId,
          file: String(track.file_path || '').split('/').pop(),
        });
        await TransportCommand.playUri(url, renderer.id, formatId, trackForTransport);
      }
    } catch (err: any) {
      console.error('[QueueController] local track dispatch failed:', err?.message || err);

      // On first failure, evict the cached URL and retry with a fresh registration.
      if (!retried && track.file_path) {
        const { invalidateCachedUrl } = await import('../lib/playback-bridge');
        invalidateCachedUrl(track.file_path);
        invalidateCachedUrl(url);
        console.log('[QueueController] retrying local track with fresh URL');
        return this._playLocalLibraryTrack(track, ctx, true);
      }

      const s2 = useMaestroStore.getState();
      if (s2.currentTrack?.id === track.id || s2.currentTrack?.file_path === track.file_path) {
        s2.setPlaybackState('paused');
      }
      return;
    }

    queueMicrotask(() => prefetchNextLocalTracks(ws, track.machine_id));
  }

  /**
   * Pre-buffer the next track on the active UPnP renderer via SetNextAVTransportURI.
   * This gives gapless playback: when the current track ends, the renderer seamlessly
   * transitions to the correct next track instead of falling back to its internal
   * queue (which causes a random-track flash on devices like NAD/BluOS).
   *
   * Non-critical — if this fails, the useRemoteAdvanceTimer fallback still works.
   */
  private async _prebufferNextTrack() {
    try {
      if (!(window as any).__TAURI__) return;

      const store = useMaestroStore.getState();
      const renderer = store.activeConnectorRenderer;
      if (!renderer) return;
      // Bluesound devices use HTTP API, not UPnP SOAP — SetNextAVTransportURI doesn't apply.
      // The Connector clears the BluOS queue before each play to prevent stale auto-advance.
      if (renderer.id.startsWith('bluesound-')) return;
      if (store.repeatMode === 'one') return;

      const { queue, queueIndex, shuffled, shuffleOrder } = store;
      let nextQueuePos = queueIndex + 1;

      if (nextQueuePos >= queue.length) {
        if (store.repeatMode === 'all') {
          nextQueuePos = 0;
        } else {
          return;
        }
      }

      const actualIdx = shuffled && shuffleOrder.length > nextQueuePos
        ? shuffleOrder[nextQueuePos] : nextQueuePos;
      const nextItem = queue[actualIdx];
      if (!nextItem?.track) return;

      const nextTrack = nextItem.track;
      if (isLocalLibraryTrack(nextTrack)) return;

      const qid = nextTrack.qobuzTrackId || nextTrack.serviceTrackId || nextTrack.id;
      if (!qid || isLocalTrackId(qid) || isLocalLibraryTrack(nextTrack)) return;

      const mz = store.multizone;
      const isMultizone = mz.enabled && mz.groupedZones.length >= 2;
      if (isMultizone) return;

      const formatId = getFormatIdForRenderer(renderer);
      const data = await useQobuzStore.getState().getStreamUrl(String(qid), formatId);
      if (!data?.url) return;

      const curStore = useMaestroStore.getState();
      if (curStore.queueIndex !== queueIndex) return;
      if (curStore.activeConnectorRenderer?.id !== renderer.id) return;

      const { setNextUri } = await import('../lib/connector');
      await setNextUri(
        renderer.id,
        data.url,
        nextTrack.title || '',
        nextTrack.artist || '',
        nextTrack.album || '',
        data.formatId || formatId,
        Math.round(nextTrack.duration || 0),
        nextTrack.artworkUrl || nextTrack.artUrl,
      );

      this._prebufferedForQueueIndex = nextQueuePos;
      console.log('[QC] Pre-buffered next track for gapless:', nextTrack.title);
    } catch (e) {
      console.debug('[QC] Pre-buffer next track failed (non-critical):', e);
      this._prebufferedForQueueIndex = -1;
    }
  }

  /**
   * Resolve a Qobuz stream URL for a track at the correct format for the
   * given renderer. Returns { url, formatId } or null.
   */
  async resolveStreamUrl(
    track: any,
    overrideRenderer?: { id: string; name?: string; manufacturer?: string } | null,
  ): Promise<{ url: string; formatId: number } | null> {
    if (isLocalLibraryTrack(track)) return null;

    const qid = track?.qobuzTrackId || track?.serviceTrackId || track?.id;
    if (!qid || isLocalTrackId(qid)) return null;

    const renderer = overrideRenderer !== undefined
      ? overrideRenderer
      : useMaestroStore.getState().activeConnectorRenderer;
    const formatId = getFormatIdForRenderer(renderer);

    try {
      const data = await useQobuzStore.getState().getStreamUrl(String(qid), formatId);
      const url = data?.url || '';
      if (!url) return null;
      return { url, formatId: data?.formatId || formatId };
    } catch {
      return null;
    }
  }

  // === Public API ===

  async next() {
    getSyncHandler().claimControl();
    this._prebufferedForQueueIndex = -1;
    this.suppressAdvance(5000);
    _lastPlayTrackCompletedAt = 0;
    const store = useMaestroStore.getState();
    const fromItem = store.queue[store.queueIndex]?.track;

    const nextIndex = store.queueIndex + 1;
    if (nextIndex < store.queue.length) {
      store.setQueueIndex(nextIndex);
      const item = store.queue[nextIndex];
      const toTr = item?.track;
      diagnosticLog(
        'info',
        'playback',
        `Auto-advance from "${fromItem?.title || ''}" to "${toTr?.title || ''}"`,
        { fromTrackId: fromItem?.id, toTrackId: toTr?.id, reason: 'skip' },
      );
      if (item?.track) await this._playTrack(item.track);
    } else if (store.repeatMode === 'all') {
      store.setQueueIndex(0);
      const item = store.queue[0];
      const toTr = item?.track;
      diagnosticLog(
        'info',
        'playback',
        `Auto-advance from "${fromItem?.title || ''}" to "${toTr?.title || ''}"`,
        { fromTrackId: fromItem?.id, toTrackId: toTr?.id, reason: 'skip' },
      );
      if (item?.track) await this._playTrack(item.track);
    } else {
      console.log('[QueueController] next() at end of playlist — stopping playback');
      store.setPlaybackState('stopped');
    }
  }

  /** @internal Exclusive poller and TransportCommand call this — must exist on QueueController */
  async advanceTrack(direction: 'next' | 'prev') {
    if (direction === 'prev') return this.previous();
    return this.next();
  }

  async previous() {
    getSyncHandler().claimControl();
    this._prebufferedForQueueIndex = -1;
    this.suppressAdvance(5000);
    _lastPlayTrackCompletedAt = 0;
    const store = useMaestroStore.getState();

    if (store.position > 3) {
      TransportCommand.seek(0);
      return;
    }

    if (store.queueIndex > 0) {
      const prevIndex = store.queueIndex - 1;
      store.setQueueIndex(prevIndex);
      const item = store.queue[prevIndex];
      if (item?.track) await this._playTrack(item.track);
    }
  }

  async playAll(tracks: any[], startIndex = 0) {
    getSyncHandler().claimControl();
    this._prebufferedForQueueIndex = -1;
    this._isPlayTrackInFlight = false;
    this._inFlightTrackId = null;
    _lastPlayTrackCompletedAt = 0;
    const store = useMaestroStore.getState();
    const queueItems = tracks.map((t: any) => ({ track: t }));
    store.setQueue(queueItems, startIndex);
    store.setActiveSource('library');
    const item = queueItems[startIndex];
    if (item?.track) await this._playTrack(item.track);
  }

  async playFrom(tracks: any[], index: number) {
    await this.playAll(tracks, index);
  }

  async playSingle(track: any) {
    getSyncHandler().claimControl();
    this._prebufferedForQueueIndex = -1;
    this._isPlayTrackInFlight = false;
    this._inFlightTrackId = null;
    _lastPlayTrackCompletedAt = 0;
    const store = useMaestroStore.getState();
    store.setQueue([{ track }], 0);
    store.setActiveSource('library');
    await this._playTrack(track);
  }

  /** True when _playTrack is actively resolving a stream URL and dispatching playback. */
  get isPlaybackInitiating(): boolean {
    return this._isPlayTrackInFlight;
  }

  async playQueueTrack(newIndex: number) {
    if (this._isPlayTrackInFlight) return;

    getSyncHandler().claimControl();
    this._prebufferedForQueueIndex = -1;
    this.suppressAdvance(5000);
    this._isPlayTrackInFlight = false;
    this._inFlightTrackId = null;
    _lastPlayTrackCompletedAt = 0;
    const store = useMaestroStore.getState();
    const item = store.queue[newIndex];
    if (!item?.track) return;
    store.setQueueIndex(newIndex);
    await this._playTrack(item.track);
  }

  appendToQueue(tracks: any[]) {
    if (!tracks.length) return;
    const items = tracks.map((t: any) => ({ track: t }));
    useMaestroStore.getState().addToQueue(items);
  }
}

export const QueueController = new QueueControllerImpl();

(window as any).QueueController = QueueController;
